import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { ConfigService } from '../services/config.service';
import { AdminAuthService } from '../admin/service/admin-auth.service';

const PROACTIVE_REFRESH_MARGIN_MS = 30 * 1000; // 30 seconds

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private adminAuthService: AdminAuthService | null = null;

  constructor(
    private authService: AuthService,
    private config: ConfigService,
    private injector: Injector
  ) {}

  private getAdminAuth(): AdminAuthService {
    if (!this.adminAuthService) {
      this.adminAuthService = this.injector.get(AdminAuthService);
    }
    return this.adminAuthService;
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // El endpoint de candidato /api/v2/process/{id} (get-process-state) lo reusa el
    // panel admin (desplegable de pasos en detalle/historial de proceso). No lleva
    // /admin/ en la URL, así que se trata como admin solo si hay sesión admin activa
    // -> le adjunta el admin_token (el authorizer Cognito lo exige, si no da 401).
    const isProcessStateReuse =
      request.url.includes('/api/v2/process/') && !!localStorage.getItem('admin_token');
    // El registro de push (ver PLAN_notificaciones-estandarizado.md) es 1 sola
    // ruta compartida entre admin/valorador y candidato -- no tiene /admin/ en
    // la URL a propósito (el tipo lo resuelve el backend consultando Cognito).
    // Le mandamos el token que exista: solo uno de los dos suele estar activo
    // en una misma sesión de navegador.
    const isPushSubscriptionEndpoint = request.url.includes('/api/v2/push-subscriptions');
    // /api/v2/maintenance/status es público; no moverlo bajo /admin/ (usaría admin_token).
    const isAdminRequest =
      request.url.includes('/admin/') ||
      isProcessStateReuse ||
      (isPushSubscriptionEndpoint && !!localStorage.getItem('admin_token'));

    if (isAdminRequest) {
      const adminAuth = this.getAdminAuth();
      if (adminAuth.getToken() && adminAuth.isTokenNearExpiry(PROACTIVE_REFRESH_MARGIN_MS)) {
        return from(adminAuth.silentRefresh().catch(() => {})).pipe(
          switchMap(() => this.handleRequest(request, next, isAdminRequest))
        );
      }
    }

    return this.handleRequest(request, next, isAdminRequest);
  }

  private handleRequest(request: HttpRequest<unknown>, next: HttpHandler, isAdminRequest: boolean): Observable<HttpEvent<unknown>> {
    const token = isAdminRequest
      ? localStorage.getItem('admin_token')
      : this.authService.getToken();

    const headers: Record<string, string> = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (this.config?.apiKey) {
      headers['x-api-key'] = this.config.apiKey;
    }

    if (Object.keys(headers).length > 0) {
      request = request.clone({ setHeaders: headers });
    }

    return next.handle(request);
  }
}
